import React, { useState, useEffect, useRef, useCallback } from 'react';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2, Circle, Plus, Trash2, Mic, MicOff, Loader2, Sparkles,
  Sun, Calendar, Cloud, Target, ChevronRight, Tag, X, Wand2, ListTree,
  Star, RotateCcw, Search, Keyboard, Send, Users, Bot, ExternalLink,
} from 'lucide-react';

const SECTIONS = [
  { id: 'today', label: 'Today', icon: Sun, accent: 'cyan' },
  { id: 'upcoming', label: 'Upcoming', icon: Calendar, accent: 'amber' },
  { id: 'someday', label: 'Someday', icon: Cloud, accent: 'violet' },
];

const PRIORITY_META = {
  high: { label: 'High', dot: 'bg-rose-400', text: 'text-rose-700 dark:text-rose-300', border: 'border-rose-200 dark:border-rose-500/40' },
  medium: { label: 'Med', dot: 'bg-amber-400', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-200 dark:border-amber-500/30' },
  low: { label: 'Low', dot: 'bg-slate-400', text: 'text-slate-600 dark:text-slate-300', border: 'border-slate-200 dark:border-slate-600' },
};

const TodoList = ({ embedded = false }) => {
  const { token } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const lightMode = !isDark;
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [todos, setTodos] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [stats, setStats] = useState({ today: 0, upcoming: 0, someday: 0, completed_today: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [focus, setFocus] = useState(null);   // {focus:[], rationale}
  const [focusLoading, setFocusLoading] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickTitle, setQuickTitle] = useState('');
  const [quickSection, setQuickSection] = useState('today');
  const [quickPriority, setQuickPriority] = useState('medium');

  // Ramble (voice) recorder state
  const [rambling, setRambling] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [rambleResult, setRambleResult] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const vadRafRef = useRef(0);
  const hasSpokenRef = useRef(false);

  const authFetch = useCallback(async (url, opts = {}) => {
    const headers = opts.headers || {};
    headers.Authorization = `Bearer ${token}`;
    return fetch(url, { ...opts, headers });
  }, [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pending, done, st] = await Promise.all([
        authFetch(`${backendUrl}/api/todos?status=pending`).then((r) => r.json()),
        authFetch(`${backendUrl}/api/todos?status=completed`).then((r) => r.json()),
        authFetch(`${backendUrl}/api/todos/stats`).then((r) => r.json()),
      ]);
      setTodos(Array.isArray(pending) ? pending : []);
      setCompleted(Array.isArray(done) ? done : []);
      setStats(st || {});
    } catch (e) { /* ignore */ } finally {
      setLoading(false);
    }
  }, [authFetch, backendUrl]);

  useEffect(() => { if (token) load(); }, [token, load]);

  // Cmd/Ctrl+K opens quick-add
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setQuickOpen(true);
      } else if (e.key === 'Escape' && quickOpen) {
        setQuickOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [quickOpen]);

  // ───── Mutations ─────
  const createTodo = async (body) => {
    const r = await authFetch(`${backendUrl}/api/todos`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (r.ok) await load();
  };

  const toggleComplete = async (todo) => {
    const url = todo.status === 'completed'
      ? `${backendUrl}/api/todos/${todo.id}/uncomplete`
      : `${backendUrl}/api/todos/${todo.id}/complete`;
    await authFetch(url, { method: 'POST' });
    await load();
  };

  const removeTodo = async (todo) => {
    await authFetch(`${backendUrl}/api/todos/${todo.id}`, { method: 'DELETE' });
    await load();
  };

  const moveTodo = async (todo, section) => {
    await authFetch(`${backendUrl}/api/todos/${todo.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ section }),
    });
    await load();
  };

  const setPriority = async (todo, priority) => {
    await authFetch(`${backendUrl}/api/todos/${todo.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ priority }),
    });
    await load();
  };

  const breakdown = async (todo) => {
    const r = await authFetch(`${backendUrl}/api/todos/breakdown`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: todo.title, notes: todo.notes }),
    });
    const d = await r.json();
    const subs = (d.subtasks || []).map((s) => ({ title: s, done: false }));
    await authFetch(`${backendUrl}/api/todos/${todo.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subtasks: [...(todo.subtasks || []), ...subs] }),
    });
    await load();
  };

  const toggleSubtask = async (todo, idx) => {
    const newSubs = [...(todo.subtasks || [])];
    newSubs[idx] = { ...newSubs[idx], done: !newSubs[idx].done };
    await authFetch(`${backendUrl}/api/todos/${todo.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subtasks: newSubs }),
    });
    await load();
  };

  const runFocus = async () => {
    setFocusLoading(true);
    try {
      const r = await authFetch(`${backendUrl}/api/todos/daily-focus`, { method: 'POST' });
      if (r.ok) setFocus(await r.json());
    } finally { setFocusLoading(false); }
  };

  // ───── Ramble (voice→task) ─────
  const startRamble = async () => {
    setRambleResult(null);
    setRambling(true);
    hasSpokenRef.current = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm',
      });
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stopVad();
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType });
        streamRef.current?.getTracks().forEach((t) => t.stop());
        if (hasSpokenRef.current && blob.size > 2048) {
          await sendRamble(blob);
        } else {
          setRambling(false);
        }
      };
      mr.start();
      mediaRecorderRef.current = mr;
      startVad(stream);
    } catch (e) {
      setRambling(false);
      alert('Microphone access denied or unavailable.');
    }
  };

  const stopRamble = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch (e) { /* ignore */ }
    }
    setRambling(false);
  };

  const startVad = (stream) => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.5;
      source.connect(analyser);
      audioCtxRef.current = ctx;
      const data = new Uint8Array(analyser.frequencyBinCount);
      const SILENCE_TH = 14;
      const SILENCE_MS = 2200;     // Ramble is longer pauses OK — 2.2s
      const MAX_MS = 45000;        // up to 45s of rambling
      const startedAt = Date.now();
      let lastSpeechAt = startedAt;
      const tick = () => {
        analyser.getByteFrequencyData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) sum += data[i];
        const avg = sum / data.length;
        const now = Date.now();
        if (avg > SILENCE_TH) { hasSpokenRef.current = true; lastSpeechAt = now; }
        const elapsed = now - startedAt;
        const since = now - lastSpeechAt;
        if (elapsed > MAX_MS) { stopRamble(); return; }
        if (hasSpokenRef.current && since > SILENCE_MS) { stopRamble(); return; }
        vadRafRef.current = requestAnimationFrame(tick);
      };
      vadRafRef.current = requestAnimationFrame(tick);
    } catch (e) { /* VAD optional */ }
  };

  const stopVad = () => {
    if (vadRafRef.current) cancelAnimationFrame(vadRafRef.current);
    vadRafRef.current = 0;
    try { audioCtxRef.current?.close(); } catch (e) { /* ignore */ }
    audioCtxRef.current = null;
  };

  const sendRamble = async (blob) => {
    setProcessing(true);
    try {
      const fd = new FormData();
      fd.append('audio', blob, 'ramble.webm');
      const r = await authFetch(`${backendUrl}/api/todos/ramble`, { method: 'POST', body: fd });
      if (!r.ok) throw new Error('Ramble failed');
      const d = await r.json();
      setRambleResult(d);
      await load();
    } catch (e) {
      setRambleResult({ error: e.message });
    } finally {
      setProcessing(false);
      setRambling(false);
    }
  };

  // ───── Render ─────
  const todosBySection = SECTIONS.reduce((acc, s) => {
    acc[s.id] = todos.filter((t) => t.section === s.id
      && (!filter || t.title.toLowerCase().includes(filter.toLowerCase())));
    return acc;
  }, {});

  return (
    <div
      className={cn(
        embedded ? '' : `min-h-screen ${lightMode ? 'todo-light-theme' : 'bg-slate-950 text-slate-100 jarvis-grid'}`
      )}
      data-testid="todo-page-root"
      data-theme={lightMode ? 'light' : 'dark'}
    >
      {!embedded && <Header />}
      <div className={embedded ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 py-6'}>
        {/* Page header */}
        <div className="mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-3">
            {!embedded && (
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="todo-title">
                  To-Do <span className="bg-gradient-to-r from-cyan-300 to-blue-400 bg-clip-text text-transparent">List</span>
                </h1>
                <p className="text-slate-400 text-sm">Stay organised. Sort by Today, Upcoming, Someday — let AI handle the rest.</p>
              </div>
            )}
            {embedded && (
              <div>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                  <span className="text-[10px] uppercase tracking-[0.25em] text-cyan-400/80 font-mono">To-Do List</span>
                </div>
                <p className="text-sm text-slate-400 mt-0.5">Capture, focus, complete.</p>
              </div>
            )}
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Filter tasks…"
                  className="pl-9 pr-3 py-2 rounded-xl bg-slate-900 border border-slate-700 focus:border-cyan-400 outline-none text-sm placeholder-slate-500"
                  data-testid="todo-filter-input"
                />
              </div>
              <Button
                onClick={() => setQuickOpen(true)}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white"
                data-testid="todo-quick-add-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Quick add
                <kbd className="ml-2 text-[10px] opacity-70 hidden sm:inline">⌘K</kbd>
              </Button>
            </div>
          </div>

          {/* Stats chips */}
          <div className="flex flex-wrap gap-2 text-xs">
            <StatChip label="Today" value={stats.today} accent="cyan" />
            <StatChip label="Upcoming" value={stats.upcoming} accent="amber" />
            <StatChip label="Someday" value={stats.someday} accent="violet" />
            <StatChip label="Done today" value={stats.completed_today} accent="emerald" />
          </div>
        </div>

        {/* AI shelf — Ramble + Daily focus */}
        {/* AI shelf — Ramble + Daily focus */}
        <div className="grid gap-3 mb-6 grid-cols-1 md:grid-cols-2">
          {/* Ramble */}
          <div
            className={`relative overflow-hidden rounded-2xl border ${rambling ? 'border-rose-500/40 bg-pastel-candy' : 'border-cyan-500/20 bg-pastel-powder'} p-4`}
            data-testid="todo-ramble-card"
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <button
                  onClick={rambling ? stopRamble : startRamble}
                  disabled={processing}
                  className={`relative w-14 h-14 rounded-full flex items-center justify-center transition-all focus:outline-none disabled:opacity-50 ${
                    rambling
                      ? 'bg-rose-600 animate-pulse ring-4 ring-rose-500/30'
                      : 'bg-navy-surface ring-2 ring-slate-900/20 hover:scale-105'
                  }`}
                  data-testid="todo-ramble-btn"
                  aria-label={rambling ? 'Stop rambling' : 'Start rambling — speak your tasks'}
                  title={rambling ? 'Pause to send' : 'Tap and speak — Ramble will turn your words into tasks'}
                >
                  {processing ? <Loader2 className="w-6 h-6 text-white animate-spin" /> : rambling ? <MicOff className="w-6 h-6 text-white" /> : <Mic className="w-6 h-6 text-white" />}
                </button>
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <Sparkles className="w-3 h-3 text-slate-900" />
                    <span className="text-[10px] uppercase tracking-[0.25em] text-slate-900 font-bold">Ramble</span>
                  </div>
                  <div className="font-extrabold text-slate-950 text-base">
                    {processing ? 'Structuring your thoughts…' : rambling ? 'Listening — speak naturally' : 'Tap and speak your tasks'}
                  </div>
                  <div className="text-xs text-slate-900/70">
                    {rambling
                      ? 'Auto-stops when you pause for ~2 seconds.'
                      : 'AI will turn your ramble into structured tasks.'}
                  </div>
                </div>
              </div>
            </div>
            {rambleResult && (
              <div className="mt-3 p-3 rounded-xl bg-white border border-slate-200" data-testid="todo-ramble-result">
                {rambleResult.error ? (
                  <div className="text-xs text-rose-600">⚠ {rambleResult.error}</div>
                ) : (
                  <>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 font-bold">Transcript</div>
                    <div className="text-xs text-slate-700 italic mb-2">"{rambleResult.transcript}"</div>
                    <div className="text-[10px] uppercase tracking-widest text-brand mb-1 font-bold">
                      Captured {rambleResult.count || 0} task{rambleResult.count === 1 ? '' : 's'}
                    </div>
                  </>
                )}
                <button onClick={() => setRambleResult(null)} className="absolute top-2 right-2 text-slate-500 hover:text-slate-900" data-testid="todo-ramble-dismiss">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Daily focus */}
          <div className="rounded-2xl border border-amber-500/30 bg-pastel-butter p-4" data-testid="todo-focus-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/40 flex items-center justify-center ring-2 ring-amber-300/30">
                  <Target className="w-6 h-6 text-amber-700" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <Sparkles className="w-3 h-3 text-slate-900" />
                    <span className="text-[10px] uppercase tracking-[0.25em] text-slate-900 font-bold">AI Daily Focus</span>
                  </div>
                  <div className="font-extrabold text-slate-950 text-base">What should I tackle first?</div>
                  <div className="text-xs text-slate-900/70">AI picks your top 3 to win the day.</div>
                </div>
              </div>
              <Button
                onClick={runFocus}
                disabled={focusLoading}
                className="bg-amber-600 hover:bg-amber-500 text-white font-bold"
                data-testid="todo-focus-btn"
              >
                {focusLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wand2 className="w-4 h-4 mr-1" /> Generate</>}
              </Button>
            </div>
            {focus && (
              <div className="mt-3 space-y-2" data-testid="todo-focus-results">
                {focus.rationale && (
                  <div className="text-xs text-slate-900/80 italic">{focus.rationale}</div>
                )}
                {(focus.focus || []).map((t, i) => (
                  <div key={t.id} className="flex items-start gap-2 p-2 rounded-lg bg-white/60 border border-amber-300/30">
                    <div className="w-6 h-6 rounded-full bg-amber-600 text-white font-bold text-xs flex items-center justify-center shrink-0">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-slate-950 truncate">{t.title}</div>
                      {t.reason && <div className="text-[10px] text-slate-700">{t.reason}</div>}
                    </div>
                  </div>
                ))}
                {(focus.focus || []).length === 0 && (
                  <div className="text-xs text-slate-900/80 italic">{focus.rationale || 'Nothing pending — enjoy the calm.'}</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sections grid — Today / Upcoming / Someday */}
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {SECTIONS.map((sec) => (
            <SectionColumn
              key={sec.id}
              section={sec}
              todos={todosBySection[sec.id]}
              loading={loading}
              onToggle={toggleComplete}
              onDelete={removeTodo}
              onMove={moveTodo}
              onSetPriority={setPriority}
              onBreakdown={breakdown}
              onToggleSub={toggleSubtask}
              onOpenLead={(leadId) => navigate(`/lead-nurture/leads/${leadId}`)}
            />
          ))}
        </div>

        {/* Completed reel */}
        {completed.length > 0 && (
          <div className="mt-8 rounded-3xl border border-slate-200 dark:border-slate-700 bg-pastel-mint dark:bg-slate-900/50 p-4 shadow-soft" data-testid="todo-completed-section">
            <h3 className="text-sm font-extrabold text-slate-900 dark:text-slate-200 mb-2 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-700 dark:text-emerald-400" /> Completed
              <span className="text-xs text-slate-700 dark:text-slate-500 font-bold">({completed.length})</span>
            </h3>
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {completed.slice(0, 30).map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg hover:bg-white/50 dark:hover:bg-slate-900/60 text-sm group" data-testid="todo-completed-row">
                  <button onClick={() => toggleComplete(t)} className="flex items-center gap-2 flex-1 min-w-0 text-left" title="Re-open">
                    <CheckCircle2 className="w-4 h-4 text-emerald-700 dark:text-emerald-400 shrink-0" />
                    <span className="text-slate-700 dark:text-slate-400 line-through truncate">{t.title}</span>
                  </button>
                  <button onClick={() => removeTodo(t)} className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 text-slate-500 hover:text-rose-600 dark:hover:text-rose-300" title="Delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer hints */}
        <div className="mt-6 flex items-center justify-center gap-3 text-[11px] text-slate-600 dark:text-slate-500 flex-wrap">
          <span className="inline-flex items-center gap-1"><Keyboard className="w-3 h-3" /> ⌘K quick add</span>
          <span>·</span>
          <span className="inline-flex items-center gap-1"><Mic className="w-3 h-3" /> Tap Ramble to dictate</span>
          <span>·</span>
          <span className="inline-flex items-center gap-1"><Wand2 className="w-3 h-3" /> AI breakdown per task</span>
          <span>·</span>
          <span className="inline-flex items-center gap-1"><Target className="w-3 h-3" /> Daily focus narrows to 3</span>
        </div>
      </div>

      {/* Quick-add modal */}
      {quickOpen && (
        <QuickAddModal
          title={quickTitle}
          setTitle={setQuickTitle}
          section={quickSection}
          setSection={setQuickSection}
          priority={quickPriority}
          setPriority={setQuickPriority}
          onCancel={() => { setQuickOpen(false); setQuickTitle(''); }}
          onSave={async () => {
            if (!quickTitle.trim()) return;
            await createTodo({ title: quickTitle.trim(), section: quickSection, priority: quickPriority });
            setQuickTitle('');
            setQuickOpen(false);
          }}
        />
      )}
    </div>
  );
};

// ────── Sub-components ───────────────────────────────────────────────

const StatChip = ({ label, value, accent }) => {
  const styles = {
    cyan: 'border-cyan-500/30 text-cyan-300 bg-cyan-500/5',
    amber: 'border-amber-500/30 text-amber-300 bg-amber-500/5',
    violet: 'border-violet-500/30 text-violet-300 bg-violet-500/5',
    emerald: 'border-emerald-500/30 text-emerald-300 bg-emerald-500/5',
  };
  return (
    <span className={`px-2.5 py-1 rounded-full border ${styles[accent]} flex items-center gap-1.5 font-mono`} data-testid={`stat-chip-${accent}`}>
      <span className="text-[10px] uppercase tracking-widest opacity-80">{label}</span>
      <span className="tabular-nums font-bold">{value || 0}</span>
    </span>
  );
};

const SectionColumn = ({ section, todos, loading, onToggle, onDelete, onMove, onSetPriority, onBreakdown, onToggleSub, onOpenLead }) => {
  const Icon = section.icon;
  const accentMap = {
    cyan: 'border-cyan-200 bg-pastel-powder dark:border-cyan-500/30 dark:bg-cyan-500/5',
    amber: 'border-amber-200 bg-pastel-butter dark:border-amber-500/30 dark:bg-amber-500/5',
    violet: 'border-violet-200 bg-pastel-lavender dark:border-violet-500/30 dark:bg-violet-500/5',
  };
  const dotMap = { cyan: 'bg-cyan-600 dark:bg-cyan-400', amber: 'bg-amber-600 dark:bg-amber-400', violet: 'bg-violet-600 dark:bg-violet-400' };
  return (
    <div className={`rounded-3xl border ${accentMap[section.accent]} p-4 min-h-[200px] shadow-soft`} data-testid={`todo-section-${section.id}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-1.5 h-1.5 rounded-full ${dotMap[section.accent]} animate-pulse`}></span>
        <Icon className="w-4 h-4 text-slate-900 dark:text-slate-300" />
        <h2 className="text-[10px] uppercase tracking-[0.25em] text-slate-900 dark:text-slate-300 font-bold">{section.label}</h2>
        <span className="text-xs text-slate-700 dark:text-slate-500 font-bold">({todos.length})</span>
      </div>
      <div className="space-y-2">
        {loading && (
          <div className="space-y-2">
            {[1, 2].map((i) => <div key={i} className="h-14 bg-white/60 dark:bg-slate-900/60 rounded-xl animate-pulse" />)}
          </div>
        )}
        {!loading && todos.length === 0 && (
          <div className="text-xs text-slate-700 dark:text-slate-500 italic py-8 text-center">No tasks here. Tap Ramble or ⌘K to add one.</div>
        )}
        {!loading && todos.map((t) => (
          <TodoCard
            key={t.id}
            todo={t}
            onToggle={onToggle}
            onDelete={onDelete}
            onMove={onMove}
            onSetPriority={onSetPriority}
            onBreakdown={onBreakdown}
            onToggleSub={onToggleSub}
            onOpenLead={onOpenLead}
          />
        ))}
      </div>
    </div>
  );
};

const TodoCard = ({ todo, onToggle, onDelete, onMove, onSetPriority, onBreakdown, onToggleSub, onOpenLead }) => {
  const [expanded, setExpanded] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const p = PRIORITY_META[todo.priority] || PRIORITY_META.medium;
  const otherSections = SECTIONS.filter((s) => s.id !== todo.section);
  const isNurture = todo.source === 'nurture';
  const isJarvis = todo.source === 'jarvis';
  const isExternal = isNurture || isJarvis;  // hides actions that only mutate the `todos` table

  const runBreakdown = async () => {
    setAiBusy(true);
    await onBreakdown(todo);
    setAiBusy(false);
    setExpanded(true);
  };

  const subDone = (todo.subtasks || []).filter((s) => s.done).length;
  const subTotal = (todo.subtasks || []).length;

  return (
    <div className={`group rounded-xl border ${p.border} bg-white dark:bg-slate-900/50 dark:hover:bg-slate-900/80 hover:shadow-soft p-2.5 transition-all`} data-testid="todo-card">
      <div className="flex items-start gap-2">
        <button onClick={() => onToggle(todo)} className="mt-0.5 shrink-0" data-testid="todo-toggle-btn">
          <Circle className="w-4 h-4 text-slate-400 hover:text-emerald-500 dark:text-slate-500 dark:hover:text-cyan-400 transition-colors" />
        </button>
        <div className="flex-1 min-w-0">
          <button onClick={() => setExpanded((v) => !v)} className="text-left w-full">
            <div className="text-sm text-slate-950 dark:text-slate-100 leading-snug font-medium">{todo.title}</div>
            {todo.scheduled_at && (
              <div className="text-[10px] text-slate-500 dark:text-slate-500 font-semibold mt-0.5">
                <Calendar className="w-2.5 h-2.5 inline mr-1" />
                {new Date(todo.scheduled_at).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })}
              </div>
            )}
          </button>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-mono uppercase ${p.text} ${p.border} border`} data-testid="todo-priority-chip">
              <span className={`w-1 h-1 rounded-full ${p.dot}`}></span>
              {p.label}
            </span>
            {todo.source === 'ramble' && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-mono uppercase text-cyan-300 border border-cyan-500/30 bg-cyan-500/5">
                <Mic className="w-2.5 h-2.5" /> Ramble
              </span>
            )}
            {isNurture && (
              <button
                onClick={(e) => { e.stopPropagation(); if (todo.lead_id) onOpenLead?.(todo.lead_id); }}
                disabled={!todo.lead_id}
                title={todo.lead_name ? `Open ${todo.lead_name}` : 'Lead follow-up'}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-mono uppercase text-fuchsia-700 dark:text-fuchsia-300 border border-fuchsia-300/60 dark:border-fuchsia-500/40 bg-fuchsia-50 dark:bg-fuchsia-500/10 hover:bg-fuchsia-100 dark:hover:bg-fuchsia-500/20 transition-colors"
                data-testid="todo-source-nurture"
              >
                <Users className="w-2.5 h-2.5" />
                {todo.lead_name || 'Lead'}
                {todo.lead_id && <ExternalLink className="w-2.5 h-2.5 opacity-70" />}
              </button>
            )}
            {isJarvis && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-mono uppercase text-violet-700 dark:text-violet-300 border border-violet-300/60 dark:border-violet-500/40 bg-violet-50 dark:bg-violet-500/10" data-testid="todo-source-derwent">
                <Bot className="w-2.5 h-2.5" /> Hey Derwent
              </span>
            )}
            {subTotal > 0 && (
              <span className="text-[10px] text-slate-500 font-mono">
                {subDone}/{subTotal} subtasks
              </span>
            )}
            {(todo.tags || []).slice(0, 3).map((tag) => (
              <span key={tag} className="text-[10px] text-slate-500 font-mono inline-flex items-center gap-0.5">
                <Tag className="w-2.5 h-2.5" /> {tag}
              </span>
            ))}
          </div>
        </div>
        {/* Quick actions — always visible on touch devices, hover-only on desktop */}
        <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-within:opacity-100 transition-opacity shrink-0">
          {subTotal === 0 && !isExternal && (
            <button
              onClick={runBreakdown}
              disabled={aiBusy}
              title="AI breakdown into subtasks"
              className="p-1 rounded text-cyan-300 hover:bg-cyan-500/10"
              data-testid="todo-breakdown-btn"
            >
              {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ListTree className="w-3.5 h-3.5" />}
            </button>
          )}
          {!isExternal && otherSections.map((s) => (
            <button
              key={s.id}
              onClick={() => onMove(todo, s.id)}
              title={`Move to ${s.label}`}
              className="p-1 rounded text-slate-400 hover:bg-slate-800"
            >
              <s.icon className="w-3.5 h-3.5" />
            </button>
          ))}
          <button onClick={() => onDelete(todo)} title="Delete" className="p-1 rounded text-slate-500 hover:text-rose-300 hover:bg-rose-500/10" data-testid="todo-delete-btn">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-2 pl-6 space-y-2">
          {todo.notes && <div className="text-xs text-slate-400">{todo.notes}</div>}
          {(todo.subtasks || []).map((s, i) => (
            <button
              key={i}
              onClick={() => onToggleSub(todo, i)}
              className="flex items-center gap-2 text-xs hover:text-slate-200 w-full text-left"
              data-testid="todo-subtask-row"
            >
              {s.done ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Circle className="w-3.5 h-3.5 text-slate-500" />}
              <span className={s.done ? 'text-slate-500 line-through' : 'text-slate-300'}>{s.title}</span>
            </button>
          ))}
          {!isExternal && (
            <div className="flex items-center gap-1 pt-1">
              <span className="text-[10px] text-slate-500 uppercase tracking-widest mr-1">Priority</span>
              {['high', 'medium', 'low'].map((p2) => (
                <button
                  key={p2}
                  onClick={() => onSetPriority(todo, p2)}
                  className={`px-2 py-0.5 text-[10px] rounded-full border ${todo.priority === p2 ? PRIORITY_META[p2].border + ' ' + PRIORITY_META[p2].text : 'border-slate-700 text-slate-500'} hover:bg-slate-800`}
                >
                  {PRIORITY_META[p2].label}
                </button>
              ))}
            </div>
          )}
          {isNurture && todo.lead_id && (
            <button
              onClick={() => onOpenLead?.(todo.lead_id)}
              className="inline-flex items-center gap-1.5 text-[11px] font-bold text-fuchsia-700 dark:text-fuchsia-300 hover:underline pt-1"
              data-testid="todo-open-lead-link"
            >
              <ExternalLink className="w-3 h-3" /> Open lead {todo.lead_name ? `· ${todo.lead_name}` : ''}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const QuickAddModal = ({ title, setTitle, section, setSection, priority, setPriority, onCancel, onSave }) => {
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  const handleKey = (e) => {
    if (e.key === 'Enter' && title.trim()) { e.preventDefault(); onSave(); }
  };
  return (
    <div
      className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-start justify-center pt-24 p-4"
      onClick={onCancel}
      data-testid="todo-quick-add-modal"
    >
      <div
        className="bg-slate-900 border border-cyan-500/30 rounded-2xl shadow-2xl w-full max-w-lg p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-cyan-300">
            <Plus className="w-4 h-4" />
            <h3 className="text-sm font-bold uppercase tracking-widest">Quick add</h3>
          </div>
          <button onClick={onCancel}><X className="w-4 h-4 text-slate-500" /></button>
        </div>
        <input
          ref={inputRef}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={handleKey}
          placeholder="What needs doing?"
          className="w-full px-4 py-3 bg-slate-950 border border-slate-700 focus:border-cyan-400 outline-none rounded-xl text-lg placeholder-slate-500"
          data-testid="todo-quick-add-input"
        />
        <div className="flex flex-wrap items-center gap-1.5 mt-3 text-xs">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 mr-1">Section</span>
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => setSection(s.id)}
              className={`px-2.5 py-1 rounded-full border ${section === s.id ? 'border-cyan-400 text-cyan-200 bg-cyan-500/10' : 'border-slate-700 text-slate-400 hover:bg-slate-800'}`}
            >
              <s.icon className="w-3 h-3 inline mr-1" /> {s.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 mt-2 text-xs">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 mr-1">Priority</span>
          {['high', 'medium', 'low'].map((p) => (
            <button
              key={p}
              onClick={() => setPriority(p)}
              className={`px-2.5 py-1 rounded-full border ${priority === p ? PRIORITY_META[p].border + ' ' + PRIORITY_META[p].text + ' bg-slate-800/70' : 'border-slate-700 text-slate-400 hover:bg-slate-800'}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full inline-block mr-1 ${PRIORITY_META[p].dot}`}></span>
              {PRIORITY_META[p].label}
            </button>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onCancel} className="border-slate-700 text-slate-300">Cancel</Button>
          <Button
            onClick={onSave}
            disabled={!title.trim()}
            className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white"
            data-testid="todo-quick-add-save"
          >
            <Send className="w-3.5 h-3.5 mr-1" /> Save
          </Button>
        </div>
        <div className="text-[10px] text-slate-500 mt-2 text-center">Press Enter to save · Esc to cancel</div>
      </div>
    </div>
  );
};

export default TodoList;
